/*
*   File : add-nominee.js
*   Author : https://evoqins.com
*   Description : Screen to fill the Nominees
*   Version : 1.0.0
*/

// import packages
import { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Modal } from "bootstrap";
import { differenceInYears } from "date-fns";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// import components
import { Icon } from "../../Components/Icon";
import { PrimaryButton, SecondaryButton } from "../../Components/Buttons";

import { ConfirmInvest, KYCConfirmationModal, } from "../../Components/Modal";

// services
import APIService from "../../Services/api-service";
import { _generate2Fa, _getProfile, _getSignupProgress } from "../../Helper/api";
import { CustomDropdown } from "../../Components/Dropdown";
import { CustomDatePicker, CustomPhoneInput, CustomTextArea, CustomTextInput, RadioGroup } from "../../Components/FormElements";
import { Breadcrumb, Loader } from "../../Components/Other";
import { Header } from "../../Components/Header";
import { _getOptions, useClickOutside } from "../../Helper/helper";


const AddNominee = () => {
    const PROGRESS_DATA = useSelector(store => store.Reducer.SIGNUP_PROGRESS);
    const GENERAL_DATA = useSelector(state => state.Reducer.GENERAL_DATA);

    const pinRef = useRef(null);

    const nomineeRef = useRef([]);
    const navigate = useNavigate();

    const [nomineeOptions, setNomineeOptions] = useState([]);
    const [guardianOptions, setGuardianOptions] = useState([]);

    const [screenLoader, setScreenLoader] = useState(true);
    const [otpLoader, setOtpLoader] = useState(false);

    const [showSuccess, setShowSuccess] = useState(false);

    const [taxPayingCountryOptions, setTaxPayingCountryOptions] = useState([]);
    const [stateData, setStateData] = useState([]);
    const [cityData, setCityData] = useState([]);

    const [identityOptions, setIdentityOptions] = useState([]);


    const [nomineeList, setNomineeList] = useState([
        {
            name: "",
            dob: "DD-MM-YYYY",
            allocation: 100,
            relation: null,
            guardian_name: "",
            // guardian_pan: "",
            guardian_relation: null,

            document_number: "",
            document: null,
            phone: "",
            email: "",
            address: "",
            country: null,
            state: null,
            city: null,
            pincode: "",

            error: ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]
        }
    ]);

    const [showOtpModal, setShowOtpModal] = useState(false);
    const [modalLoader, setModalLoader] = useState(false);

    const [visibleOptionsData, setVisibleOptionsData] = useState([]);
    const [visibleOption, setVisibleOption] = useState(null);

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    // Define the disabled days rule
    const disabledDays = { after: yesterday };

    const [showDropdown, setShowDropdown] = useState(false);

    useClickOutside(pinRef, () => {
        setShowDropdown(false);
    })


    useEffect(() => {
        if (Object.keys(PROGRESS_DATA).length !== 0) {

            if (PROGRESS_DATA.kyc_data.is_nominee_available === true) {
                const nominee_list = PROGRESS_DATA.kyc_data.nominee.map((item) => {
                    const IS_MINOR = differenceInYears(new Date(), new Date(item.dob)) < 18;

                    return ({
                        name: item.name,
                        dob: item.dob,
                        allocation: item.allocation,
                        relation: { label: item.relation, value: item.relation_id },
                        guardian_name: item.guardian_name !== null ? item.guardian_name : "",
                        // guardian_pan: item.guardian_pan !== null ? item.guardian_pan.toLocaleUpperCase() : "",
                        guardian_relation: item.guardian_relation !== null ? { label: item.guardian_relationship, value: item.guardian_relationship_id } : "",
                        error: ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""],
                        document_number: IS_MINOR == true ?
                            item.guardian_id_proof_number !== null ? item.guardian_id_proof_number : ""
                            : item.identity_proof_number !== null ? item.identity_proof_number : "",
                        document: IS_MINOR == true ?
                            item.guardian_id_proof_type !== null ? { label: item.guardian_id_proof_type_text, value: item.guardian_id_proof_type } : null
                            : item.identity_proof_type !== null ? { label: item.identity_proof_type_text, value: item.identity_proof_type } : null,
                        phone: IS_MINOR == true ?
                            item.guardian_contact_detail.phone_number !== null ? item.guardian_contact_detail.phone_number : ""
                            :
                            item.contact_detail.phone_number != null ? item.contact_detail.phone_number : "",
                        email: IS_MINOR == true ?
                            item.guardian_contact_detail.email !== null ? item.guardian_contact_detail.email : ""
                            :
                            item.contact_detail.email != null ? item.contact_detail.email : "",
                        address: IS_MINOR == true ?
                            item.guardian_address.address_line1 !== null ? item.guardian_address.address_line1 : ""
                            :
                            item.nominee_address.address_line1 != null ? item.nominee_address.address_line1 : "",
                        country: IS_MINOR == true ?
                            item.guardian_address.country_id !== null ? { label: item.guardian_address.country, value: item.guardian_address.country_id } : null
                            :
                            item.nominee_address.address_line1 != null ? { label: item.nominee_address.country, value: item.nominee_address.country_id } : null,
                        state: IS_MINOR == true ?
                            item.guardian_address.state_id !== null ? { label: item.guardian_address.state_name, value: item.guardian_address.state_id } : null
                            :
                            item.nominee_address.state_id != null ? { label: item.nominee_address.state_name, value: item.nominee_address.state_id } : null,
                        city: IS_MINOR == true ?
                            item.guardian_address.city_id !== null ? { label: item.guardian_address.city, value: item.guardian_address.city_id } : null
                            :
                            item.nominee_address.city_id != null ? { label: item.nominee_address.city, value: item.nominee_address.city_id } : null,
                        pincode: IS_MINOR == true ?
                            item.guardian_address.pincode !== null ? item.guardian_address.pincode : ""
                            :
                            item.nominee_address.pincode != null ? item.nominee_address.pincode : "",
                    })
                });
                setNomineeList(nominee_list);
            }
        }
    }, [PROGRESS_DATA]);

    useEffect(() => {
        if (Object.keys(GENERAL_DATA).length !== 0) {
            // account types options
            const kyc_relationship = _getOptions(GENERAL_DATA.kyc_relationship);
            setNomineeOptions(kyc_relationship);

            const nominee_minor_relation = _getOptions(GENERAL_DATA.nominee_minor_relation);
            setGuardianOptions(nominee_minor_relation);
            if (kyc_relationship && nominee_minor_relation) {
                setScreenLoader(false);
            }

            if (GENERAL_DATA.nominee_identity_proof) {

                let updated_proof_list = GENERAL_DATA.nominee_identity_proof.map(item => ({
                    value: item.id,
                    label: item.display_name
                }))
                setIdentityOptions(updated_proof_list);
            }

            if (GENERAL_DATA.nominations_info_visibility) {

                let updated_ifo_visibility = GENERAL_DATA.nominations_info_visibility.map(item => ({
                    id: item.id,
                    name: item.display_name
                }))
                setVisibleOptionsData(updated_ifo_visibility);

                setVisibleOption(GENERAL_DATA.nominations_info_visibility[0].id)
            }
        }
    }, [GENERAL_DATA]);

    useEffect(() => {
        if (showSuccess === true) {
            const modal = new Modal(document.getElementById("confirm"));
            modal.show();
        }
    }, [showSuccess]);

    useEffect(() => {
        if (showOtpModal === true) {
            const modal = new Modal(document.getElementById("confirm-invest"));
            modal.show();
        }
    }, [showOtpModal]);

    useEffect(() => {
        _getCountries();
        _getState();
    }, []);

    // handle add nominee 
    function _handleAddNominee() {
        const nominee_list = [...nomineeList]
        nominee_list.push({
            name: "",
            dob: "DD-MM-YYYY",
            allocation: "",
            relation: null,
            guardian_name: "",
            // guardian_pan: "",
            guardian_relation: null,

            document_number: "",
            document: null,
            phone: "",
            email: "",
            address: "",
            country: null,
            state: null,
            city: null,
            pincode: "",

            error: ["", "", "", "", "", "", "", "", "", "", "", "", "", "", "", ""]
        });
        setNomineeList(nominee_list);
    }

    // handle remove nominee 
    function _handleRemoveNominee(index) {
        const nominee_list = [...nomineeList]
        nominee_list.splice(index, 1)
        setNomineeList(nominee_list);
    }

    // handle nominee values 
    function _handleNominee(item_value, key, index, error_index) {

        if (key === "state") {
            _getCity(item_value.value);
        }

        if (key === "pincode") {
            _searchState(item_value)
        }

        const nominee_list = [...nomineeList]
        nominee_list[index][key] = item_value;
        nominee_list[index]['error'][error_index] = "";

        if (key === "country") {
            nominee_list[index]["state"] = null;
            nominee_list[index]["city"] = null;
            nominee_list[index]["pincode"] = "";
        }

        if (key === "document") {
            nominee_list[index]["document_number"] = "";
        }

        setNomineeList(nominee_list);
    }

    // handle confirm investment
    function _confirmNominee(status) {
        setShowSuccess(status);
    }


    // navigate to home screen
    function _handleParentNavigate() {
        navigate("/");
    }

    // navigate to parent screen
    function _handleCurrentNavigate() {
        navigate("/nominee");
    }

    function _handleDate(date) {
        const [day, month, year] = date.split('-');
        const start_date = new Date(`${year}-${month}-${day}`);
        return start_date
    }

    const parseDate = (dateString) => {
        const [day, month, year] = dateString.split('-');
        return new Date(year, month - 1, day);
    };

    const [isSameInvestor, setIsSameInvestor] = useState({});

    const _handleSameInvestor = (index) => {
        setIsSameInvestor((prev) => {
            const nextState = !prev[index];
            const updatedState = { ...prev, [index]: nextState };

            if (PROGRESS_DATA) {
                const address_data = PROGRESS_DATA?.kyc_data?.address?.permanent_address;

                setNomineeList((prevList) => {
                    const updatedList = [...prevList];
                    updatedList[index] = {
                        ...updatedList[index],
                        address: nextState ? address_data.address_line1 : null,
                        country: nextState ? { value: address_data.country_id, label: address_data.country } : null,
                        state: nextState ? { value: address_data.state_id, label: address_data.state } : null,
                        city: nextState ? { value: address_data.city_id, label: address_data.city } : null,
                        pincode: nextState ? address_data.pincode : "",
                    };
                    return updatedList;
                });
            }

            return updatedState;
        });
    };

    const _handleDetail = (index) => {
        if (pinData !== null) {
            setNomineeList((prevList) => {
                const updatedList = [...prevList];
                updatedList[index] = {
                    ...updatedList[index],
                    state: { value: pinData.state_id, label: pinData.state },
                    city: { value: pinData.city_id, label: pinData.city }
                };
                return updatedList;
            });
        }
    };

    // handle validate nominees
    function _validateNominees() {
        let valid = true;
        const validate_pan = /^[A-Z]{3}P[A-Z]{1}\d{4}[A-Z]{1}$/; // validation of PAN
        let is_scrolled = false;

        const nominee_list = [...nomineeList]
        for (let i = 0; i < nominee_list.length; i++) {

            const element = nominee_list[i];
            const IS_MINOR = differenceInYears(new Date(), new Date(element.dob)) < 18;

            if (element.name === "") {
                nominee_list[i].error[0] = "Nominee name is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }
            if (element.relation === null) {
                nominee_list[i].error[1] = "Relation is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }
            if (element.dob === "DD-MM-YYYY") {
                nominee_list[i].error[2] = "Date of Birth is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }
            if (element.allocation === "" || element.allocation < 1) {
                nominee_list[i].error[3] = "Allocation is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }
            if (IS_MINOR === true && element.guardian_name === "") {
                nominee_list[i].error[4] = "Guardian name is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }
            // if (IS_MINOR === true && element.guardian_pan === "") {
            //     nominee_list[i].error[5] = "Guardian PAN number is required"
            //     valid = false
            //     if (!is_scrolled) {
            //         nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
            //         is_scrolled = true;
            //     }
            // }
            // if (IS_MINOR === true && element.guardian_pan.toLocaleUpperCase() !== "" && !validate_pan.test(element.guardian_pan.toLocaleUpperCase())) {
            //     nominee_list[i].error[5] = "Invalid PAN number"
            //     valid = false;
            //     if (!is_scrolled) {
            //         nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
            //         is_scrolled = true;
            //     }
            // }
            if (IS_MINOR === true && element.guardian_relation === null) {
                nominee_list[i].error[6] = "Choose relation with nominee (guardian relation)"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.document === null) {
                nominee_list[i].error[8] = "Select identity document"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.document_number === "") {
                nominee_list[i].error[7] = "Document number is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }



            if (element.document !== null && element.document?.label === "Pan" && element.document_number !== "" && !validate_pan.test(element.document_number.toLocaleUpperCase())) {
                nominee_list[i].error[7] = "Invalid PAN"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.phone === "") {
                nominee_list[i].error[9] = "Phone number is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.phone !== "" && element.phone.length < 10) {
                nominee_list[i].error[9] = "Invalid phone"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.email === "") {
                nominee_list[i].error[10] = "Email address is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.email !== "" && !/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(element.email)) {
                nominee_list[i].error[10] = "Invalid email"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.address === "") {
                nominee_list[i].error[11] = "Address is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.country === null) {
                nominee_list[i].error[12] = "Country is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.state === null || element.state === "") {
                nominee_list[i].error[13] = "State is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.city === null || element.city === "") {
                nominee_list[i].error[14] = "City is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

            if (element.pincode === "") {
                nominee_list[i].error[15] = "Pincode is required"
                valid = false
                if (!is_scrolled) {
                    nomineeRef.current[i].scrollIntoView({ block: "start", behavior: "smooth" });
                    is_scrolled = true;
                }
            }

        }


        setNomineeList(nominee_list);

        if (valid === true) {
            const sum = nominee_list.reduce((a, b) => parseInt(a) + parseInt(b.allocation), 0);


            if (sum !== 100) {
                toast.error("Total allocation should be 100%", {
                    type: "error"
                });
                valid = false;
            } else {
                _generate2FaApi();
            }
        }
    }

    const _handleVisibleOption = (id) => {
        setVisibleOption(id)
    };

    // API - otp
    const _generate2FaApi = () => {
        setOtpLoader(true);
        _generate2Fa("Nominee")
            .then((response) => {
                setShowOtpModal(true);
            }).catch((error) => {
                toast.dismiss();
                toast.error(error, {
                    type: "error",
                });
            });
        setOtpLoader(false)
    };

    // API - get countries
    const _getCountries = () => {
        const url = "/general/countries";

        APIService(false, url).then((response) => {
            if (response.status_code === 200) {
                const tax_paying_countries = [];


                response.data.countries_for_address.map((item) => {
                    return (
                        tax_paying_countries.push({
                            label: item.name,
                            value: item.id
                        })
                    );
                });
                setTaxPayingCountryOptions(tax_paying_countries);
            }
        });
    };

    //API- Get state
    const _getState = () => {
        let is_post = false;
        let url = '/general/states';
        let data = null;

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const state_options = _getOptions(response.data);
                setStateData(state_options);
            } else {
                setStateData([]);
            }
        });
    };

    //API- Post City
    const _getCity = (state_id) => {
        let is_post = true;
        let url = '/general/cities';
        let data = {
            "state_id": state_id,
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                const city_options = _getOptions(response.data);
                setCityData(city_options);

            } else {
                setCityData([]);
            }
        });
    };

    // const [pin, setPin] = useState("");
    const [pinData, setPinData] = useState(null);
    const [searchData, setSearchData] = useState(true);


    //API- search state
    const _searchState = (code) => {
        let is_post = true;
        let url = '/general/search-state';
        let data = {
            pincode: code
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setPinData(response.data);
                setSearchData(true);
            } else {
                setPinData(null);
                if (code.length === 6) {
                    setSearchData(false);
                }
            }
        });
    }

    // API -save nominee
    const _saveNominee = async (otp) => {
        setModalLoader(true);
        var nominee_data = [];

        toast.dismiss();

        const url = "/profile/upsert-nominee";


        // nominee_data = nomineeList.map((item) => {
        //     const MINOR = differenceInYears(new Date(), new Date(item.dob)) < 18;
        //     return ({
        //         name: item.name,
        //         relation_id: item.relation.value,
        //         dob: item.dob,
        //         guardian_name: MINOR === true ? item.guardian_name : null,
        //         guardian_relationship_id: MINOR === true ? item.guardian_relation.value : null,
        //         guardian_pan: MINOR === true ? item.guardian_pan : null,
        //         allocation: item.allocation
        //     })
        // })

        nominee_data = nomineeList.map((item) => {
            const MINOR = differenceInYears(new Date(), new Date(item.dob)) < 18;
            return ({
                name: item.name,
                relation_id: item.relation.value,
                "dob": item.dob,
                "allocation": item.allocation,

                "guardian_name": MINOR === true ? item.guardian_name : null,
                "guardian_relationship_id": MINOR === true ? item.guardian_relation.value : null,

                "guardian_id_proof_type": MINOR ? item.document.value : null,
                "guardian_id_proof_number": MINOR ? item.document_number : null,

                "identity_proof_type": MINOR ? null : item.document.value,
                "identity_proof_number": MINOR ? null : item.document_number,

                ...(MINOR ?
                    {
                        "guardian_address": {
                            "address_line1": item.address,
                            "state_id": item.state.value,
                            "state_name": item.country.value === 251 ? item.state.label : item.state,
                            "city_id": item.city.value,
                            "city": item.country.value === 251 ? item.city.label : item.city,
                            "pincode": item.pincode,
                            "country_id": item.country.value
                        }
                    }
                    : {
                        "guardian_address": {
                            "address_line1": null,
                            "state_id": null,
                            "state_name": null,
                            "city_id": null,
                            "city": null,
                            "pincode": null,
                            "country_id": null
                        }
                    }
                ),

                ...(MINOR ?
                    {
                        "guardian_contact_detail": {
                            "email": item.email,
                            "country_code": "+91",
                            "phone_number": item.phone
                        }
                    }
                    :
                    {
                        "guardian_contact_detail": {
                            "email": null,
                            "country_code": null,
                            "phone_number": null
                        }
                    }
                ),


                "nominee_address": {
                    "address_line1": item.address,
                    "state_id": item.state.value,
                    "state_name": item.country.value === 251 ? item.state.label : item.state,
                    "city_id": item.city.value,
                    "city": item.country.value === 251 ? item.city.label : item.city,
                    "pincode": item.pincode,
                    "country_id": item.country.value
                },

                "contact_detail": {
                    "email": item.email,
                    "country_code": "+91",
                    "phone_number": item.phone
                },

            })
        })

        const request = {
            "nominee": nominee_data,
            "is_nominee_available": true,
            "nominations_info_visibility": visibleOption,
            "otp": otp
        }

        try {
            const response = await APIService(true, url, request);
            if (response.status_code === 200) {
                _getSignupProgress().then((response) => {
                    _confirmNominee(true);
                    _getProfile();
                })
                return true;
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                });
                setModalLoader(false);
                return false;
            }
        } catch (error) {
            setModalLoader(false);
            return false;
        }
    };


    return (
        screenLoader === true ?
            <Loader height="h-80vh" />
            :
            <Fragment>
                <div className="bg-white border-radius-tl-40px border-radius-bl-40px">
                    <div className="row ">
                        <div className="col-12 padding-144px-bottom">
                            <Header type={2} />
                            <div className="px-4 ">
                                <Breadcrumb mainPage="Home"
                                    currentPage="Manage Nominee"
                                    childPage={"Nominee Details"}
                                    handleParentNavigate={_handleParentNavigate}
                                    handleCurrentNavigate={_handleCurrentNavigate}
                                    type={2} />
                                <p className="e-montserrat-semi-bold e-font-18 e-line-height-38 color-dark-jungle-green margin-30px-top mb-0">Nominee Details</p>
                                <div className="row d-flex justify-content-center">

                                    <div className="col-lg-6">

                                        <Fragment>
                                            {
                                                nomineeList.map((item, index) => {
                                                    return (
                                                        <div ref={el => nomineeRef.current[index] = el}
                                                            key={index}>
                                                            <div className="d-flex justify-content-between align-items-center my-4">
                                                                <p className="e-montserrat-semi-bold e-font-16 e-line-height-24 color-eerie-black mb-0">Nominee-{index + 1}</p>
                                                                {
                                                                    nomineeList.length > 1 &&
                                                                    <Icon icon="delete"
                                                                        size={24}
                                                                        className="cursor-pointer"
                                                                        onClick={() => _handleRemoveNominee(index)} />
                                                                }
                                                            </div>

                                                            <div className="mb-4">
                                                                <CustomTextInput label="Nominee holder name"
                                                                    postfix="*"
                                                                    value={item.name}
                                                                    error={item.error[0]}
                                                                    placeholder="Nominee name"
                                                                    handleChange={(input_value) => _handleNominee(input_value, 'name', index, 0)} />
                                                            </div>


                                                            <div className="mb-4">
                                                                <CustomDropdown label="Nominee relationship"
                                                                    postfix="*"
                                                                    options={nomineeOptions}
                                                                    value={item.relation}
                                                                    error={item.error[1]}
                                                                    message="No nominee found"
                                                                    placeholder="Relation with nominee"
                                                                    onSelectChange={(select_value) => _handleNominee(select_value, 'relation', index, 1)} />
                                                            </div>


                                                            <div className="mb-4">

                                                                <CustomDatePicker error={item.error[2]}
                                                                    disabledDays={disabledDays}
                                                                    label="Date of birth"
                                                                    postfix={true}
                                                                    start={item.dob}
                                                                    defaultMonth={item.dob !== "DD-MM-YYYY" ? _handleDate(item.dob) : null}
                                                                    mode="single"
                                                                    selectDate={(date) => {
                                                                        const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
                                                                        const start_date = new Date(date);
                                                                        const formatted_start_date = start_date.toLocaleDateString('en-GB', options).replace(/\//g, '-');
                                                                        _handleNominee(formatted_start_date, 'dob', index, 2)

                                                                    }} />
                                                            </div>

                                                            <div className="mb-4">

                                                                <CustomTextInput label="Allocation"
                                                                    postfix="*"
                                                                    value={item.allocation}
                                                                    error={item.error[3]}
                                                                    type="number"
                                                                    placeholder="Allocation %"
                                                                    handleChange={(input_value) => {
                                                                        if (input_value <= 100) {
                                                                            _handleNominee(input_value, 'allocation', index, 3)
                                                                        }
                                                                    }} />
                                                            </div>
                                                            {differenceInYears(new Date(), new Date(parseDate(item.dob))) < 18 &&
                                                                <Fragment>
                                                                    <div className="mb-4">

                                                                        <CustomTextInput label="Guardian name"
                                                                            postfix="*"
                                                                            value={item.guardian_name}
                                                                            error={item.error[4]}
                                                                            placeholder="Name"
                                                                            handleChange={(input_value) => {
                                                                                _handleNominee(input_value, 'guardian_name', index, 4)
                                                                            }} />
                                                                    </div>

                                                                    <div className="mb-4">
                                                                        <CustomDropdown label="Guardian relationship"
                                                                            postfix="*"
                                                                            options={guardianOptions}
                                                                            value={item.guardian_relation}
                                                                            error={item.error[6]}
                                                                            placeholder="Guardian relationship"
                                                                            message="No guardian found"
                                                                            onSelectChange={(select_value) => _handleNominee(select_value, 'guardian_relation', index, 6)} />
                                                                    </div>
                                                                    {/* <div className="mb-4">
                                                                        <CustomTextInput label="Guardian PAN Number"
                                                                            postfix="*"
                                                                            maxLength={10}
                                                                            value={item.guardian_pan}
                                                                            error={item.error[5]}
                                                                            placeholder="PAN"
                                                                            inputClass="text-transform-upper"
                                                                            handleChange={(input_value) => _handleNominee(input_value, 'guardian_pan', index, 5)} />
                                                                    </div> */}
                                                                </Fragment>
                                                            }


                                                            <CustomDropdown label="Select identity document"
                                                                postfix="*"
                                                                options={identityOptions}
                                                                value={item.document}
                                                                message="No data found"
                                                                error={item.error[8]}
                                                                placeholder="Select identity document"
                                                                onSelectChange={(select_value) => _handleNominee(select_value, 'document', index, 8)} />

                                                            <div className="pt-4">
                                                                <CustomTextInput label="Identity document number"
                                                                    postfix="*"
                                                                    error={item.error[7]}
                                                                    value={item.document_number}
                                                                    type={item.document !== null ? (item.document?.label == "Pan" || item.document?.label == "Passport" || item.document?.label == "Driving licence") ? "text" : "number" : "text"}
                                                                    maxLength={item.document !== null ? item.document?.label == "Pan" ? 10 : item.document?.label == "Passport" ? 9 : item.document?.label == "Driving licence" ? 20 : 4 : undefined}
                                                                    inputClass={item.document !== null && item.document?.label == "Pan" ? "text-transform-upper" : ""}
                                                                    placeholder="Enter number"
                                                                    handleChange={(input_value) => _handleNominee(input_value, 'document_number', index, 7)} />
                                                            </div>
                                                            <div className="pt-4">
                                                                <CustomPhoneInput label="Phone number"
                                                                    postfix="*"
                                                                    placeholder="Enter phone number"
                                                                    value={item.phone}
                                                                    maxLength={10}
                                                                    type="number"
                                                                    error={item.error[9]}
                                                                    handleChange={(input_value) => _handleNominee(input_value, 'phone', index, 9)} />
                                                            </div>

                                                            <div className="pt-4">
                                                                <CustomTextInput label="Email address"
                                                                    postfix="*"
                                                                    value={item.email}
                                                                    error={item.error[10]}
                                                                    placeholder="Enter email address"
                                                                    handleChange={(input_value) => _handleNominee(input_value, 'email', index, 10)} />
                                                            </div>

                                                            <div className={`pt-4 d-flex gap-2 align-items-center cursor-pointer w-max-content`}>
                                                                <Icon
                                                                    icon={isSameInvestor[index] ? 'checked' : 'unchecked'}
                                                                    size={18}
                                                                    className="d-flex"
                                                                    onClick={() => _handleSameInvestor(index)} />
                                                                <span className={`e-font-14 e-montserrat-medium e-line-height-20px color-eerie-black`}>
                                                                    Same as investor address
                                                                </span>
                                                            </div>

                                                            <div className="py-4">
                                                                <CustomTextArea label="Address line 1"
                                                                    postfix="*"
                                                                    value={item.address}
                                                                    rows={3}
                                                                    error={item.error[11]}
                                                                    placeholder="Enter address"
                                                                    handleChange={(input_value) => _handleNominee(input_value, 'address', index, 11)} />
                                                            </div>

                                                            <div className="pb-4">
                                                                <CustomDropdown label="Country"
                                                                    postfix="*"
                                                                    options={taxPayingCountryOptions}
                                                                    value={item.country}
                                                                    isSearchable={true}
                                                                    message="No country found"
                                                                    error={item.error[12]}
                                                                    placeholder="Select your Country"
                                                                    onSelectChange={(select_value) => _handleNominee(select_value, 'country', index, 12)} />

                                                            </div>

                                                            {item.country !== null && item.country.value !== 251 ?
                                                                <CustomTextInput label="Pincode"
                                                                    postfix="*"
                                                                    value={item.pincode}
                                                                    error={item.error[15]}
                                                                    placeholder="Enter pincode"
                                                                    handleChange={(input_value) => _handleNominee(input_value, 'pincode', index, 15)} />
                                                                :
                                                                <div className="position-relative">
                                                                    <CustomTextInput label="Pincode"
                                                                        value={item.pincode}
                                                                        error={item.error[15]}
                                                                        type="number"
                                                                        placeholder="Enter pincode"
                                                                        postfix="*"
                                                                        handleChange={(input_value) => _handleNominee(input_value, 'pincode', index, 15)}
                                                                        onFocus={() => {
                                                                            setShowDropdown(true);
                                                                        }}
                                                                    />
                                                                    {showDropdown === true &&
                                                                        <div className="position-absolute top-80px w-100 box-shadow-black p-3 bg-white z-index-9 border-radius-16px " ref={pinRef}>

                                                                            {searchData === true ?
                                                                                <Fragment>
                                                                                    <p className={`mb-0 e-color-eerie-black e-font-14 e-montserrat-regular e-line-height-24`}>
                                                                                        Pin : <span className="e-montserrat-semi-bold">{item.pincode}</span>
                                                                                    </p>
                                                                                    <p className={`mb-0 e-color-eerie-black e-font-14 e-montserrat-regular e-line-height-24`}>
                                                                                        State : <span className="e-montserrat-semi-bold">{pinData !== null ? pinData.state : "-"}</span>, City: <span className="e-montserrat-semi-bold">{pinData !== null ? pinData.city : "-"}</span>
                                                                                    </p>

                                                                                    <div className="text-center">
                                                                                        <PrimaryButton label="Continue" className="px-3 mt-3 mx-auto"
                                                                                            onPress={() => {
                                                                                                _handleDetail(index);
                                                                                                setShowDropdown(false);
                                                                                            }} />
                                                                                    </div>
                                                                                </Fragment>
                                                                                :
                                                                                <Fragment>

                                                                                    <p className={`mb-0 e-color-eerie-black e-font-14 e-montserrat-regular e-line-height-24`}>
                                                                                        No data found, please fill the State and City manually
                                                                                    </p>
                                                                                    <div className="text-center">
                                                                                        <PrimaryButton label="Continue" className="px-3 mt-3 mx-auto"
                                                                                            onPress={() => {
                                                                                                _handleDetail(index);
                                                                                                setShowDropdown(false);
                                                                                            }} />
                                                                                    </div>
                                                                                </Fragment>
                                                                            }

                                                                        </div>
                                                                    }
                                                                </div>

                                                            }

                                                            <div className="row pt-4 position-relative">
                                                                <div className="col-sm-6">
                                                                    {item.country !== null && item.country.value !== 251 ?
                                                                        <CustomTextInput label="State"
                                                                            postfix="*"
                                                                            value={item.state}
                                                                            error={item.error[13]}
                                                                            placeholder="Enter state"
                                                                            handleChange={(input_value) => _handleNominee(input_value, 'state', index, 13)} />
                                                                        :
                                                                        <CustomDropdown label="State"
                                                                            postfix="*"
                                                                            options={stateData}
                                                                            value={item.state}
                                                                            isSearchable={true}
                                                                            message="No nominee found"
                                                                            error={item.error[13]}
                                                                            placeholder="Select state"
                                                                            onSelectChange={(select_value) => _handleNominee(select_value, 'state', index, 13)} />

                                                                    }

                                                                </div>
                                                                <div className="col-sm-6">
                                                                    {item.country !== null && item.country.value !== 251 ?
                                                                        <CustomTextInput label="City"
                                                                            postfix="*"
                                                                            value={item.city}
                                                                            error={item.error[14]}
                                                                            placeholder="Enter city"
                                                                            handleChange={(input_value) => _handleNominee(input_value, 'city', index, 14)} />
                                                                        :
                                                                        <div className={`${!item.state ? "opacity-50 pe-none" : ""}`}>
                                                                            <CustomDropdown label="City"
                                                                                postfix="*"
                                                                                options={cityData}
                                                                                value={item.city}
                                                                                isSearchable={true}
                                                                                message="No city found"
                                                                                error={item.error[14]}
                                                                                placeholder="Select your city"
                                                                                onSelectChange={(select_value) => _handleNominee(select_value, 'city', index, 14)} />
                                                                        </div>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            }


                                            <div className="mt-4 d-flex align-items-center gap-4px e-bg-light-lavender padding-10px-tb px-2 border-radius-44px e-font-14 e-line-height-18 e-montserrat-medium w-max-content color-rebecca-purple cursor-pointer" onClick={_handleAddNominee}>
                                                <Icon icon="add"
                                                    size={24} />
                                                Add another nominee
                                            </div>


                                            <p className={`pt-4 pb-2 mb-1 e-montserrat-medium color-dark-charcoal e-font-14 e-line-height-18 cursor-text bg-transparent`}>
                                                Nominee visibility on account statement
                                                <span className='color-deep-carmine-pink'>
                                                    *
                                                </span>
                                            </p>
                                            <RadioGroup data={visibleOptionsData}
                                                selected={visibleOption}
                                                handleSelect={_handleVisibleOption} />

                                            <div className="d-flex align-items-start gap-8px e-bg-papaya-whip mt-4 border-radius-12px padding-14px-all">
                                                <span>
                                                    <Icon icon="info-circle-yellow"
                                                        size={18}
                                                        color="#C75802"
                                                        className="min-w-18px"
                                                    />
                                                </span>
                                                <p className="e-montserrat-medium e-font-14 e-line-height-24 color-dark-golden mb-0">Adding a nominee ensures your chosen person will receive your assets, simplifying the transfer process when needed.</p>
                                            </div>


                                        </Fragment>
                                        <div className="border-top-1px border-bright-gray my-md-4 my-3" />
                                        <div className="row">
                                            <div className="col-6">
                                                <SecondaryButton label="Cancel"
                                                    className="padding-12px-tb w-100"
                                                    onPress={_handleCurrentNavigate} />
                                            </div>
                                            <div className="col-6">
                                                <PrimaryButton label="Confirm"
                                                    className="padding-12px-tb w-100"
                                                    showLoader={otpLoader}
                                                    disabled={otpLoader}
                                                    onPress={_validateNominees} />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {showOtpModal === true &&
                    <ConfirmInvest closeModal={() => setShowOtpModal(false)}
                        verify={_saveNominee}
                        loader={modalLoader} />
                }

                {
                    showSuccess &&
                    <KYCConfirmationModal title={nomineeList.length === 0 ? "Nominee added successfully" : "Nominee updated successfully"}
                        type={1}
                        showButton={true}
                        content=""
                        closeModal={() => _confirmNominee(false)}
                        handleNavigate={_handleCurrentNavigate} />
                }

            </Fragment >
    )
}
export default AddNominee